import type { Identifier } from "ra-core";

/**
 * Whose records a client view shows. An account aggregates every contact of
 * the company; a contact shows only records linked to that person.
 */
export type ClientScope =
  | { kind: "company"; companyId: Identifier }
  | { kind: "contact"; contactId: Identifier; companyId?: Identifier | null };

export const companyScope = (companyId: Identifier): ClientScope => ({
  kind: "company",
  companyId,
});

export const contactScope = (
  contactId: Identifier,
  companyId?: Identifier | null,
): ClientScope => ({ kind: "contact", contactId, companyId });

/** Records with their own `company_id` and `contact_id` (invoices, proposals, contracts, tickets, subscriptions). */
export const clientRecordFilter = (scope: ClientScope) =>
  scope.kind === "company"
    ? { "company_id@eq": scope.companyId }
    : { "contact_id@eq": scope.contactId };

/** Projects list their people in `contact_ids`. */
export const clientDealFilter = (scope: ClientScope) =>
  scope.kind === "company"
    ? { "company_id@eq": scope.companyId }
    : { "contact_ids@cs": `{${scope.contactId}}` };

/** Notes, tasks and meetings belong to contacts. */
export const contactIdsFilter = (contactIds: Identifier[]) =>
  contactIds.length > 0
    ? { "contact_id@in": `(${contactIds.join(",")})` }
    : { "contact_id@eq": -1 };

export const clientScopeKey = (scope: ClientScope) =>
  scope.kind === "company"
    ? `company:${scope.companyId}`
    : `contact:${scope.contactId}`;

/** Company used for account-level links (new invoice, proposal). */
export const clientScopeCompanyId = (scope: ClientScope) =>
  scope.companyId ?? null;

export const clientScopeContactId = (scope: ClientScope) =>
  scope.kind === "contact" ? scope.contactId : null;
